import datadog from "@ilg-core/analytics/datadog"
import { useEffect } from 'react';
import { onLogout } from "./auth";
import { useSession } from "next-auth/react";
import { Session } from "next-auth";
import { isLocalEnv, isProductionEnv } from "@/utilities/helpers";
import { Cookies } from "react-cookie";
import { Static } from "@ilg-credability/credability-web-components";
import { CallerEnums } from "@/types/Analytics";

type SessionStorageProperty = { key: string; value: string };

export const baseBFF = () => {
  return 'bff'
}

export function setHeaders(token: string) {
  return {
    Accept: "application/json",
    "content-type": "application/json",
    Authorization: `Bearer ${token}`,
    "api-version": "3.0"
  }
}

const errorHandling = async (res: Response, url: string | URL, expectingJsonResponse = true) => {
  const error = new Error('An error occurred while fetching the data.')
  // Attach extra info to the error object.
  try {
    const errorDetails = expectingJsonResponse ? await res.json() : 'No response body expected'
    error.cause = JSON.stringify(errorDetails)
  } catch (e) {
    error.cause = e
  }

  if (res.status < 500) { // covers 4xx responses such as 'unauthorised'
    datadog.log.warn(`API Failure: ${url}`, { cause: error.cause })
  } else {
    // covers 5xx server errors
    Object.assign(error, { status: res.status, statusText: res.statusText })
    datadog.log.error(`API Failure: ${url}`, {
      statusCode: res.status,
      statusMessage: res.statusText,
      http: { status_code: res.status },
      cause: error.cause,
    })
  }

  throw error
}

interface FetcherOptions {
  allowedStatuses?: number[];
}

export const fetcher = async (
  url: string | URL,
  token: string,
  method: "GET" | "POST" | "PUT" = "GET",
  body?: any,
  options?: FetcherOptions
) => {
  if (token) {
    const res = await fetch(url, {
      method,
      headers: { ...setHeaders(token) },
      ...(body && { body: JSON.stringify(body) }),
    })

    if (!res.ok) {
      if (options?.allowedStatuses?.includes(res.status)) {
        return null;
      }
      await errorHandling(res, url)
    }

    try {
      return await res.json();
    } catch (error) {
      // If .json() fails, it's likely because the response body is empty or not valid JSON.
      datadog.log.warn('Failed to parse JSON response in fetcher', {
        url: String(url),
        message: error instanceof Error ? error.message : String(error)
      });
      return undefined;
    }
  } else {
    datadog.log.warn(`Token Missing: ${url}`)
  }
}

export const fetchWithNoResponse = async (
  url: string | URL,
  token: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body?: any,
) => {
  if (token) {
    const res = await fetch(url, {
      method,
      ...(body && { body: JSON.stringify(body) }),
      headers: { ...setHeaders(token) }
    })

    if (!res.ok) {
      await errorHandling(res, url, false)
    }
  } else {
    datadog.log.warn(`Token Missing: ${url}`)
  }
}

// Constants for session management
const SESSION_IDLE_TIMEOUT = 3 * 60 * 1000; // 3 minutes in milliseconds
const SESSION_KEEP_ALIVE_INTERVAL = 3 * 60 * 1000; // 3 minutes in milliseconds

export function useSessionKeepAliveAndIdle() {
  const { data: session, update } = useSession();
  useEffect(() => {
    let idleTimeout: NodeJS.Timeout;
    let keepAliveInterval: NodeJS.Timeout;

    const handleActivity = () => {
      // Reset idle timer
      if (idleTimeout) {
        clearTimeout(idleTimeout);
      }
      idleTimeout = setTimeout(handleIdle, SESSION_IDLE_TIMEOUT);
      // Reset keep-alive interval
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
      }
      keepAliveInterval = setInterval(refreshSession, SESSION_KEEP_ALIVE_INTERVAL);
    };

    const handleIdle = () => {
      // If the document doesn't have focus the user is likely interacting with
      // an embedded iframe (journey pages). Reschedule instead of logging out.
      if (!document.hasFocus()) {
        idleTimeout = setTimeout(handleIdle, SESSION_IDLE_TIMEOUT);
        return;
      }
      datadog.log.info('User idle timeout reached, initiating logout');
      session?.idToken && onLogout(session?.idToken);
    };

    const refreshSession = async () => {
      try {
        update()
      } catch (error: unknown) {
        const parsedError = error instanceof Error ? error : new Error(String(error));
        datadog.log.error('Session refresh failed', {
          cause: parsedError.cause,
          statusMessage: parsedError.message,
          stack: parsedError.stack,
        });
        // If session refresh fails, also log out
        session?.idToken && onLogout(session?.idToken);
      }
    };

    // Add multiple event listeners for different types of user activity
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    // Initial idle timer
    idleTimeout = setTimeout(handleIdle, SESSION_IDLE_TIMEOUT);
    // Initial keep-alive interval
    keepAliveInterval = setInterval(refreshSession, SESSION_KEEP_ALIVE_INTERVAL);

    // Cleanup on component unmount
    return () => {
      if (idleTimeout) {
        clearTimeout(idleTimeout);
      }
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
      }
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [session, update]);
}

export const setAccessTokenInCookie = (session: Session) => {
  const cookies = new Cookies();

  cookies.set("access-token", session?.accessToken, {
    domain: isLocalEnv()
      ? "localhost"
      : `credability${!isProductionEnv() ? "-test" : ""}.co.uk`,
    expires: session?.expires ? new Date(session.expires) : undefined, // if expires is undefined, the cookie will be a session cookie
    secure: !isLocalEnv(),
    sameSite: "strict",
  });
}

/* build CC URL */
export function buildProductURL(sessionToken: string, baseURL: string, journeyType: string | CallerEnums) {
  let urlParams = "";
  // get params from session storage
  const savedParams = window.sessionStorage.getItem("urlParams") ?? "";
  // parse session storage params
  const params: SessionStorageProperty[] = savedParams
    ? JSON.parse(savedParams)
    : [];
  if (params.length) {
    // loop through params and add to URL
    params.forEach((param: SessionStorageProperty) => {
      urlParams += `${param.key}=${param.value}&`;
    });
  }
  return `${baseURL}?token=${sessionToken}&journeyType=${journeyType}&iframe=true${urlParams ? `&${urlParams}` : ''}`;
}